"use client";

import { useRouter } from "next/navigation";
import { Icon } from "@to/ui";

/**
 * Property-detail v1 re-skin — the slim utility row above the gallery: a Back
 * control on the left; the listing's status badge + MLS number on the right.
 * (The v1 app carried these in a custom sticky page header; AD keeps the shared
 * site shell, so they live at the top of the page content instead — per the
 * user's layout decision.)
 *
 * Client component: Back is `router.back()` (the visitor usually arrives from
 * an agent profile's listings, the open-house map, or the home explorer — a
 * hardcoded destination would lose that context).
 *
 * Status chips map the sync's NORMALIZED statuses (`active` / `pending` /
 * `closed`, lowercased by `normalizeListingStatus`): closed renders as "Sold"
 * on a muted chip; active → success; pending → sun; anything else → neutral.
 * Theme tokens only.
 */

const STATUS_CHIP: Record<string, { label: string; background: string }> = {
  active: { label: "Active", background: "var(--brand-success)" },
  pending: { label: "Pending", background: "var(--brand-sun)" },
  closed: { label: "Sold", background: "var(--brand-text-muted)" },
};

export function PropertyTopBar({
  status,
  mlsListingId,
}: {
  status: string | null;
  mlsListingId: string | null;
}) {
  const router = useRouter();
  const chip = status
    ? (STATUS_CHIP[status] ?? {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        background: "var(--brand-text-muted)",
      })
    : null;

  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => router.back()}
        className="inline-flex items-center gap-1.5 font-medium"
        style={{ color: "var(--brand-text-muted)", fontSize: "14px" }}
      >
        <Icon name="arrow_back" size={16} />
        Back
      </button>

      <div className="flex items-center gap-3">
        {chip && (
          <span
            className="inline-flex items-center rounded-full px-3 py-1 font-bold uppercase"
            style={{
              backgroundColor: chip.background,
              color: "var(--brand-primary-foreground)",
              fontSize: "11px",
              letterSpacing: "0.04em",
            }}
          >
            {chip.label}
          </span>
        )}
        {mlsListingId && (
          <span
            className="hidden sm:inline"
            style={{ color: "var(--brand-text-muted)", fontSize: "12px" }}
          >
            MLS# {mlsListingId}
          </span>
        )}
      </div>
    </div>
  );
}
